/**
 * Backend Gamification Client
 *
 * This module replaces firebase-gamification.ts by making API calls to the oxigen-backend
 * instead of directly accessing Firebase Firestore.
 *
 * All gamification data is now stored in PostgreSQL via the backend.
 */

import { getTenantContext } from '@/lib/tenant-context';

// ============================================================================
// TYPES & INTERFACES (Matching backend entity structure)
// ============================================================================

export interface UserGamification {
  id: string;
  tenantId: string;
  userId: string | null;
  redmineUserId: number;
  username: string;
  email: string | null;
  schemaVersion: number;
  level: number;
  levelName: string;
  currentXp: number;
  totalXp: number;
  monthlyXp: number;
  weeklyXp: number;
  lifetimeXp: number;
  currentStreak: number;
  longestStreak: number;
  lastActivityDate: string | null;
  lastSyncDate: string | null;
  lastEvaluatedAt: string | null;
  activeDaysCount: number;
  processedActivityIds: string[];
  badges: string[];
  badgesUnlocked: BadgeUnlock[];
  achievements: AchievementProgress[];
  dailyChallenges: any[];
  weeklyMissions: any[];
  statistics: UserStatistics;
  monthlyStatistics: UserStatistics;
  weeklyStatistics: UserStatistics;
  leaderboardPositions: LeaderboardPositions;
  seasonHistory: Record<string, SeasonHistoryEntry>;
  createdAt: string;
  updatedAt: string;
}

export interface BadgeUnlock {
  key: string;
  unlockedAt: string;
}

export interface SeasonHistoryEntry {
  xp: number;
  rank: number;
  badges: string[];
}

export interface AchievementProgress {
  achievementKey: string;
  progress: number;
  maxProgress: number;
  completed: boolean;
  completedAt: string | null;
}

export interface UserStatistics {
  monthlyIssuesCreated: number;
  monthlyIssuesClosed: number;
  monthlyHoursLogged: number;
  monthlyBugsFixed: number;
  monthlyCommentsAdded: number;
  monthlyReviewsCompleted: number;
  monthlyEfficientClosures: number;
  monthlyQualityDescriptions: number;
  monthlyEarlyMorningLogs: number;
  monthlyOnTimeCompletions: number;
  monthlyTasksCompleted: number;
  monthlyFeaturesCompleted: number;
  monthlyAttachmentsUploaded: number;
  monthlyWikiPagesCreated: number;
  lifetimeIssuesCreated: number;
  lifetimeIssuesClosed: number;
  lifetimeHoursLogged: number;
  lifetimeBugsFixed: number;
  lifetimeCommentsAdded: number;
  lifetimeReviewsCompleted: number;
  lifetimeEfficientClosures: number;
  lifetimeTeammatesHelped: number;
  lifetimeWikiPagesCreated: number;
  lifetimeAttachmentsUploaded: number;
  lifetimeTimeEntries: number;
}

export interface LeaderboardPositions {
  daily: number;
  weekly: number;
  monthly: number;
  allTime: number;
}

export interface Achievement {
  id: string;
  key: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  rarity: string;
  xpReward: number;
  unlockCondition: string;
  maxProgress: number;
  resetType: string;
  isHidden: boolean;
  isActive: boolean;
}

export interface LeaderboardEntry {
  odingerId: number;
  username: string;
  email: string | null;
  level: number;
  xp: number;
  streak: number;
  badgeCount: number;
  rank: number;
}

export interface ActivityLog {
  id: string;
  tenantId: string;
  profileId: string | null;
  redmineUserId: number;
  username: string;
  activityType: string;
  description: string;
  xpEarned: number;
  monthKey: string;
  metadata: Record<string, any>;
  createdAt: string;
}

// ============================================================================
// CONSTANTS (Same as firebase-gamification.ts)
// ============================================================================

export const CURRENT_SCHEMA_VERSION = 2;

export const LEVEL_THRESHOLDS = [
  { level: 1, name: 'New Dawn', minXP: 0, maxXP: 49 },
  { level: 2, name: 'First Steps', minXP: 50, maxXP: 149 },
  { level: 3, name: 'Bright Spark', minXP: 150, maxXP: 299 },
  { level: 4, name: 'Swift Current', minXP: 300, maxXP: 499 },
  { level: 5, name: 'Rising Flame', minXP: 500, maxXP: 749 },
  { level: 6, name: 'Steel Edge', minXP: 750, maxXP: 1099 },
  { level: 7, name: 'Wild Spirit', minXP: 1100, maxXP: 1499 },
  { level: 8, name: 'Silent Storm', minXP: 1500, maxXP: 1999 },
  { level: 9, name: 'Mystic Wanderer', minXP: 2000, maxXP: 2599 },
  { level: 10, name: 'Ocean Tide', minXP: 2600, maxXP: 3299 },
  { level: 11, name: 'Crimson Wing', minXP: 3300, maxXP: 4099 },
  { level: 12, name: 'Jade Sentinel', minXP: 4100, maxXP: 4999 },
  { level: 13, name: 'Azure Dragon', minXP: 5000, maxXP: 5999 },
  { level: 14, name: 'Frost Monarch', minXP: 6000, maxXP: 7199 },
  { level: 15, name: 'Shadow Weaver', minXP: 7200, maxXP: 8599 },
  { level: 16, name: 'Golden Phoenix', minXP: 8600, maxXP: 10199 },
  { level: 17, name: 'Thunder Lord', minXP: 10200, maxXP: 11999 },
  { level: 18, name: 'Celestial Knight', minXP: 12000, maxXP: 13999 },
  { level: 19, name: 'Void Walker', minXP: 14000, maxXP: 16199 },
  { level: 20, name: 'Divine Champion', minXP: 16200, maxXP: 18599 },
  { level: 21, name: 'Eternal Guardian', minXP: 18600, maxXP: 21199 },
  { level: 22, name: 'Astral Sage', minXP: 21200, maxXP: 23999 },
  { level: 23, name: 'Omega Warrior', minXP: 24000, maxXP: 26999 },
  { level: 24, name: 'Infinity Seeker', minXP: 27000, maxXP: 30199 },
  { level: 25, name: 'Cosmic Emperor', minXP: 30200, maxXP: 33599 },
  { level: 26, name: 'Primordial Titan', minXP: 33600, maxXP: 37199 },
  { level: 27, name: 'Reality Bender', minXP: 37200, maxXP: 40999 },
  { level: 28, name: 'Time Weaver', minXP: 41000, maxXP: 44999 },
  { level: 29, name: 'Dimension Walker', minXP: 45000, maxXP: 49199 },
  { level: 30, name: 'Universe Shaper', minXP: 49200, maxXP: 53599 },
  { level: 31, name: 'Nexus Guardian', minXP: 53600, maxXP: 58199 },
  { level: 32, name: 'Quantum Master', minXP: 58200, maxXP: 62999 },
  { level: 33, name: 'Ethereal Sovereign', minXP: 63000, maxXP: 67999 },
  { level: 34, name: 'Absolute Zero', minXP: 68000, maxXP: 73199 },
  { level: 35, name: 'Transcendent Apex', minXP: 73200, maxXP: Infinity },
];

export const XP_VALUES = {
  ISSUE_CREATE: 5,
  ISSUE_CREATE_QUALITY: 15,
  ISSUE_CLOSE: 15,
  ISSUE_CLOSE_BONUS: 30,
  TIME_LOG: 3,
  TIME_LOG_QUALITY: 5,
  TIME_LOG_DESCRIPTION: 10,
  COMMENT: 5,
  ATTACHMENT: 3,
  STREAK_BONUS: 5,
};

// ============================================================================
// API CLIENT
// ============================================================================

async function apiCall<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const tenant = getTenantContext();
  if (!tenant) {
    throw new Error('No tenant context available');
  }

  const url = `${tenant.backendUrl}/api/gamification/${tenant.id}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`API call failed: ${response.status} - ${error}`);
  }

  return response.json();
}

// ============================================================================
// USER PROFILE FUNCTIONS
// ============================================================================

/**
 * Get user gamification profile
 */
export async function getUserGamification(
  odingerId: string
): Promise<UserGamification | null> {
  try {
    const profile = await apiCall<UserGamification | null>(
      `/profile/${odingerId}`
    );
    return profile;
  } catch (error) {
    console.error('[Gamification] Failed to get profile:', error);
    return null;
  }
}

/**
 * Initialize user gamification profile (create if not exists)
 */
export async function initializeUserGamification(
  odingerId: string,
  username: string,
  email: string
): Promise<UserGamification> {
  const profile = await apiCall<UserGamification>(`/profile/${odingerId}`, {
    method: 'POST',
    body: JSON.stringify({ username, email }),
  });
  return profile;
}

/**
 * Subscribe to user gamification updates (polling-based for backend)
 * Returns a function to unsubscribe
 */
export function subscribeToUserGamification(
  odingerId: string,
  callback: (data: UserGamification | null) => void,
  pollInterval: number = 30000
): () => void {
  let isActive = true;

  const poll = async () => {
    if (!isActive) return;

    try {
      const data = await getUserGamification(odingerId);
      if (isActive) {
        callback(data);
      }
    } catch (error) {
      console.error('[Gamification] Poll error:', error);
    }

    if (isActive) {
      setTimeout(poll, pollInterval);
    }
  };

  // Initial fetch
  poll();

  // Return unsubscribe function
  return () => {
    isActive = false;
  };
}

// ============================================================================
// XP FUNCTIONS
// ============================================================================

/**
 * Award XP to user
 */
export async function awardXP(
  odingerId: string,
  xpAmount: number,
  activityType: string,
  description: string,
  metadata?: Record<string, any>
): Promise<{ leveledUp: boolean; newLevel: number; profile: UserGamification }> {
  const result = await apiCall<{
    leveledUp: boolean;
    newLevel: number;
    profile: UserGamification;
  }>(`/profile/${odingerId}/xp`, {
    method: 'POST',
    body: JSON.stringify({
      xpAmount,
      activityType,
      description,
      metadata,
    }),
  });
  return result;
}

/**
 * Get XP configuration for tenant
 */
export async function getXPConfig(): Promise<Record<string, number>> {
  const config = await apiCall<Record<string, number>>(`/config/xp`);
  return config;
}

// ============================================================================
// STREAK FUNCTIONS
// ============================================================================

/**
 * Update user streak
 */
export async function updateStreak(
  odingerId: string
): Promise<{ streakIncreased: boolean; currentStreak: number }> {
  const result = await apiCall<{
    streakIncreased: boolean;
    currentStreak: number;
  }>(`/profile/${odingerId}/streak`, {
    method: 'PUT',
  });
  return result;
}

// ============================================================================
// ACHIEVEMENT FUNCTIONS
// ============================================================================

/**
 * Get all achievement definitions
 */
export async function getAllAchievements(): Promise<Achievement[]> {
  const tenant = getTenantContext();
  if (!tenant) {
    throw new Error('No tenant context available');
  }

  const response = await fetch(`${tenant.backendUrl}/api/gamification/achievements`, {
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`Failed to get achievements: ${response.status}`);
  }

  return response.json();
}

/**
 * Update achievement progress (increment)
 */
export async function updateAchievementProgress(
  odingerId: string,
  achievementKey: string,
  progressIncrement: number = 1
): Promise<{ completed: boolean; achievement: Achievement | null }> {
  const result = await apiCall<{
    completed: boolean;
    achievement: Achievement | null;
  }>(`/profile/${odingerId}/achievements/${achievementKey}`, {
    method: 'PUT',
    body: JSON.stringify({ progressIncrement }),
  });
  return result;
}

/**
 * Set achievement progress (exact value)
 */
export async function setAchievementProgress(
  odingerId: string,
  achievementKey: string,
  progressValue: number
): Promise<{ completed: boolean; achievement: Achievement | null }> {
  const result = await apiCall<{
    completed: boolean;
    achievement: Achievement | null;
  }>(`/profile/${odingerId}/achievements/${achievementKey}`, {
    method: 'PUT',
    body: JSON.stringify({ progressValue }),
  });
  return result;
}

// ============================================================================
// LEADERBOARD FUNCTIONS
// ============================================================================

/**
 * Get leaderboard
 */
export async function getLeaderboard(
  timeframe: 'daily' | 'weekly' | 'monthly' | 'allTime' = 'allTime',
  limit: number = 50
): Promise<LeaderboardEntry[]> {
  const entries = await apiCall<LeaderboardEntry[]>(
    `/leaderboard?timeframe=${timeframe}&limit=${limit}`
  );
  return entries;
}

// ============================================================================
// ACTIVITY LOG FUNCTIONS
// ============================================================================

/**
 * Get user activity logs
 */
export async function getUserActivityLogs(
  odingerId: string,
  limit: number = 20,
  monthFilter: boolean = true
): Promise<ActivityLog[]> {
  const logs = await apiCall<ActivityLog[]>(
    `/profile/${odingerId}/activity-logs?limit=${limit}&monthFilter=${monthFilter}`
  );
  return logs;
}

/**
 * Create activity log (done automatically by awardXP, but can be called directly)
 */
export async function createActivityLog(
  _odingerId: string,
  _activityType: string,
  _description: string,
  _xpEarned: number,
  _metadata?: Record<string, any>
): Promise<void> {
  // Activity logs are created automatically by the backend when awarding XP
  // This function is kept for API compatibility but does nothing
  console.log(
    '[Gamification] createActivityLog is deprecated - logs are created automatically'
  );
}

// ============================================================================
// RESET FUNCTIONS (Admin only)
// ============================================================================

/**
 * Perform monthly reset for all profiles in tenant
 */
export async function performMonthlyReset(): Promise<{ resetCount: number }> {
  const result = await apiCall<{ resetCount: number }>(`/reset/monthly`, {
    method: 'POST',
  });
  return result;
}

/**
 * Perform weekly reset for all profiles in tenant
 */
export async function performWeeklyReset(): Promise<{ resetCount: number }> {
  const result = await apiCall<{ resetCount: number }>(`/reset/weekly`, {
    method: 'POST',
  });
  return result;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate level from total XP
 */
export function calculateLevelFromXP(totalXP: number): {
  level: number;
  name: string;
  currentXP: number;
  xpForNextLevel: number;
  progress: number;
} {
  const levelInfo =
    LEVEL_THRESHOLDS.find((l) => totalXP >= l.minXP && totalXP <= l.maxXP) ||
    LEVEL_THRESHOLDS[LEVEL_THRESHOLDS.length - 1];

  const xpInLevel = totalXP - levelInfo.minXP;
  const xpForLevel = levelInfo.maxXP - levelInfo.minXP + 1;
  const progress = Math.min((xpInLevel / xpForLevel) * 100, 100);

  return {
    level: levelInfo.level,
    name: levelInfo.name,
    currentXP: xpInLevel,
    xpForNextLevel: xpForLevel,
    progress,
  };
}

/**
 * Get level info
 */
export function getLevelInfo(level: number): {
  name: string;
  minXP: number;
  maxXP: number;
} {
  const info = LEVEL_THRESHOLDS.find((l) => l.level === level);
  return info || LEVEL_THRESHOLDS[0];
}

/**
 * Check if today is a working day (not weekend)
 */
export function isWorkingDay(date: Date = new Date()): boolean {
  const day = date.getDay();
  return day !== 0 && day !== 6; // Not Sunday (0) or Saturday (6)
}

/**
 * Get current month key (YYYY-MM format)
 */
export function getCurrentMonthKey(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

// Export ACHIEVEMENTS_DEFINITIONS for compatibility
export { ACHIEVEMENTS_DEFINITIONS } from './achievements-definitions';
